import { Button } from "@prelude-ai/design-system"
import { Plus, Trash2 } from "lucide-react"

export type FlowStateEntry = { id: string; key: string; value: string }

let nextId = 0
export function createEntry(key = "", value = ""): FlowStateEntry {
  return { id: String(nextId++), key, value }
}

type FlowStateEditorProps = {
  entries: FlowStateEntry[]
  onChange: (entries: FlowStateEntry[]) => void
  title?: string
}

export function parseEntryValue(value: string): unknown {
  const trimmed = value.trim()
  if (!trimmed) return ""
  try {
    return JSON.parse(trimmed) as unknown
  } catch {
    return trimmed
  }
}

function KeyInput({ value, onChange }: { value: string; onChange: (val: string) => void }) {
  return (
    <input
      type="text"
      placeholder="key"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 min-w-0 rounded border border-input bg-background px-2 py-1 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-ring"
    />
  )
}

export default function FlowStateEditor({ entries, onChange, title = "Initial Flow State" }: FlowStateEditorProps) {
  const updateEntry = (index: number, field: "key" | "value", val: string) => {
    const next = entries.map((e, i) => (i === index ? { ...e, [field]: val } : e))
    onChange(next)
  }

  const removeEntry = (index: number) => {
    onChange(entries.filter((_, i) => i !== index))
  }

  const addEntry = () => {
    onChange([...entries, createEntry()])
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-2 border-b border-border bg-muted/30">
        <span className="text-xs font-medium text-muted-foreground">{title}</span>
      </div>
      <div className="flex-1 overflow-auto p-3 space-y-2">
        {entries.map((entry, i) => (
          <div key={entry.id} className="flex items-start gap-1.5">
            <KeyInput value={entry.key} onChange={(val) => updateEntry(i, "key", val)} />
            <input
              type="text"
              placeholder="value"
              value={entry.value}
              onChange={(e) => updateEntry(i, "value", e.target.value)}
              className="flex-[2] min-w-0 rounded border border-input bg-background px-2 py-1 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => removeEntry(i)}>
              <Trash2 className="h-3 w-3 text-muted-foreground" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="w-full text-xs gap-1.5" onClick={addEntry}>
          <Plus className="h-3 w-3" />
          Add field
        </Button>
      </div>
    </div>
  )
}
